/**
 * Shared `queryOptions` for the org-scoped queries the authenticated pages read.
 *
 * Each factory is the single definition of a query's key AND fetcher, so a route
 * `loader` can `ensureQueryData(...)` the exact query its component later reads
 * with `useQuery(...)` — the two cannot drift apart and silently double-fetch.
 * Pass the `orgId` from the route context (`_authenticated`'s `activeOrg`), not
 * from a client-side org lookup: the server fns resolve the org from the session
 * themselves, so `orgId` here only scopes the cache entry.
 */
import { type QueryClient, queryOptions } from '@tanstack/react-query'

import { usageKeys } from '@/components/usage/keys'
import { getOrgPlanStatus } from '@/lib/billing/server'
import {
  getProxyConnectionInfo,
  listCredentials,
} from '@/lib/credentials/server'
import { getActiveOrgBalance, getAuthContext } from '@/lib/org/server'
import { getOrgUsage } from '@/lib/usage/server'
import type { UsageRange } from '@/lib/usage/usage'

/**
 * The `_authenticated` guard's auth context, wrapped as a Query.
 *
 * `beforeLoad` calling `getAuthContext()` directly has nothing to dedupe it, and
 * hover-preload runs the guard once while the click runs it AGAIN — measured as
 * `getAuthContext` firing twice, back to back, on every tab switch. At ~90ms per
 * round trip off-LAN that was the bulk of the switch latency. Going through Query
 * collapses both into one cache read, and the SSR query integration dehydrates it
 * into the page payload so the first client navigation is warm too.
 *
 * `staleTime` is deliberately short. This guard is UI routing, NOT the security
 * boundary — every server fn independently re-reads the session via
 * `requireOrgMember` — so the only exposure is authenticated chrome painting for a
 * few seconds after a session dies, before its data calls 401. 15s dwarfs a
 * hover→click (well under 1s) while keeping that window small.
 *
 * Anything that changes the session's identity or active org MUST invalidate this
 * key, or pages render against the wrong org: see `OrgSwitcher` (org switch) and
 * `NavUser` (sign-out).
 */
export const authContextQuery = queryOptions({
  queryKey: ['auth-context'],
  queryFn: () => getAuthContext(),
  staleTime: 15_000,
})

export const orgQueries = {
  planStatus: (orgId: string | null) =>
    queryOptions({
      queryKey: ['org-plan', orgId],
      queryFn: () => getOrgPlanStatus(),
    }),
  balance: (orgId: string | null) =>
    queryOptions({
      queryKey: ['org-balance', orgId],
      queryFn: () => getActiveOrgBalance(),
    }),
  credentials: (orgId: string | null) =>
    queryOptions({
      queryKey: ['credentials', orgId],
      queryFn: () => listCredentials(),
    }),
  proxyConnectionInfo: () =>
    queryOptions({
      queryKey: ['proxy-connection-info'],
      queryFn: () => getProxyConnectionInfo(),
    }),
  usage: (orgId: string | null, range: UsageRange) =>
    queryOptions({
      queryKey: usageKeys.orgUsage(orgId, range),
      queryFn: () => getOrgUsage({ data: { range } }),
    }),
}

/**
 * Refresh the cached auth context after something changed the session's
 * identity or active org, BEFORE `router.invalidate()` re-runs the guard.
 *
 * It has to be a `fetchQuery` with the full options and `staleTime: 0`.
 * Nothing observes this query with `useQuery` (the guard reads it with
 * `ensureQueryData`), and on a page that was server-rendered the entry was
 * hydrated from the SSR payload, so the client-side query has NO queryFn of
 * its own. `invalidateQueries` therefore only marks it stale (with
 * `refetchType: 'all'` it tries to refetch and hangs on the missing queryFn),
 * and the guard's `ensureQueryData` then hands back the stale entry, since it
 * returns cached data whenever any exists: every page renders the PREVIOUS
 * org until a full reload. `fetchQuery` supplies the queryFn, `staleTime: 0`
 * forces the request, and awaiting it means the guard reads the new org.
 */
export async function refreshAuthContext(
  queryClient: QueryClient,
): Promise<void> {
  await queryClient.fetchQuery({ ...authContextQuery, staleTime: 0 })
}
